import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  LogIn,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import StickyMobileCTA from "./StickyMobileCTA";
import BackToTop from "./BackToTop";
import { Reveal } from "./Reveal";
import { useAuth } from "../context/AuthContext";

/**
 * Agency Portal entry — simplified.
 *
 * Auth flow is intentionally single-step: agencies create an account ONCE, then
 * shop the institutional menu and submit their Purchase Order at checkout.
 * There is no separate "access request" form (previously redundant with signup).
 *
 * - Anonymous visitors: see credentials / how it works + Sign In / Create
 *   Account CTAs
 * - Logged-in agency users: auto-redirected to /menu (institutional pricing
 *   already auto-applied server-side)
 */
export const AgencyPortalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAgency = user && (user.role === "agency" || user.role === "admin");

  useEffect(() => {
    if (isAgency) {
      navigate("/menu", { replace: true });
    }
  }, [isAgency, navigate]);

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-14 bg-brand-charcoal grain">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <Link
                to="/"
                data-testid="agency-back-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
              >
                ← Back to Home
              </Link>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-teal/15 border border-brand-teal/40 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand-teal">
                <ShieldCheck className="h-3.5 w-3.5" />
                Institutional / Agency Portal
              </div>
            </Reveal>
            <Reveal delay={2}>
              <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.04]">
                Agency Catering.
                <br className="hidden sm:block" />
                <span className="text-brand-gold">PO-ready.</span>
              </h1>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-5 max-w-xl text-[15px] lg:text-[17px] text-white/75 leading-relaxed">
                Institutional catering for NYC public schools, city agencies,
                nonprofits, and corporate buyers. Purchase Order accepted. Net
                terms available for approved accounts.
              </p>
            </Reveal>

            <Reveal delay={4}>
              <div className="mt-7 grid sm:grid-cols-3 gap-3 max-w-2xl">
                {[
                  { label: "DOE Vendor", note: "NYC Dept. of Education" },
                  { label: "MWBE Certified", note: "Minority & Women-Owned" },
                  { label: "NYC Vendor", note: "City Agency Approved" },
                ].map((c) => (
                  <div
                    key={c.label}
                    className="rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3"
                  >
                    <p className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-brand-gold">
                      <BadgeCheck className="h-3.5 w-3.5" />
                      {c.label}
                    </p>
                    <p className="mt-0.5 text-[11px] text-white/55">{c.note}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={5}>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/agency/login"
                  data-testid="agency-login-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-5 py-3 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Agency Sign In
                </Link>
                <Link
                  to="/agency/register"
                  data-testid="agency-register-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white px-5 py-3 text-[11.5px] font-bold uppercase tracking-[0.12em] transition-colors border border-white/15"
                >
                  Create Agency Account
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* "How it works" — 3-step explainer */}
        <section className="py-14 sm:py-16 bg-brand-gray/40">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
                How it works
              </p>
              <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl text-brand-charcoal tracking-tight">
                Three steps from sign-up to served.
              </h2>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {[
                {
                  n: "1",
                  title: "Create your agency account",
                  body: "Sign up once with your agency name, contact, and email. Open self-registration — no waiting on manual approval to start shopping.",
                  cta: { label: "Create Account", href: "/agency/register", testid: "agency-step1-cta" },
                },
                {
                  n: "2",
                  title: "Shop the institutional menu",
                  body: "Browse every catering item with institutional pricing automatically applied to your account. Add half-pans, sliders, sides, beverages — everything you need for the room.",
                  cta: { label: "View Menu", href: "/agency/login", testid: "agency-step2-cta" },
                },
                {
                  n: "3",
                  title: "Submit your PO at checkout",
                  body: "Enter your Purchase Order number at checkout — no card required. Our team confirms receipt within one business day and invoices on your standard terms.",
                  cta: null,
                },
              ].map((s) => (
                <Reveal key={s.n} delay={parseInt(s.n)}>
                  <article className="h-full rounded-3xl bg-white border border-black/5 p-6 sm:p-7 shadow-[0_18px_40px_-26px_rgba(17,17,17,0.18)]">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-charcoal text-brand-gold font-display font-black text-[14px]">
                      {s.n}
                    </span>
                    <h3 className="mt-4 font-display font-black text-[18px] sm:text-[20px] text-brand-charcoal tracking-tight leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] text-brand-charcoal/70 leading-relaxed">
                      {s.body}
                    </p>
                    {s.cta ? (
                      <Link
                        to={s.cta.href}
                        data-testid={s.cta.testid}
                        className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                      >
                        {s.cta.label} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    ) : null}
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={4}>
              <div className="mt-12 rounded-3xl bg-white border border-black/5 p-6 sm:p-8 grid sm:grid-cols-[auto_1fr] gap-5 items-center">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <h4 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
                    Approved for institutional catering across NYC.
                  </h4>
                  <p className="mt-1.5 text-[13.5px] text-brand-charcoal/70 leading-relaxed">
                    NYC DOE Vendor · MWBE Certified · NYC City Vendor approved.
                    Used by public schools, city agencies, nonprofits, and corporate
                    teams across the five boroughs.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Final CTA strip */}
        <section className="py-14 sm:py-16 bg-brand-charcoal">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 text-center">
            <Reveal>
              <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-gold">
                Ready to feed the room?
              </p>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl text-white tracking-tight">
                Set up your agency account in under a minute.
              </h2>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/agency/register"
                  data-testid="agency-final-register-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors"
                >
                  Create Agency Account
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/agency/login"
                  data-testid="agency-final-login-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors border border-white/15"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Sign In
                </Link>
              </div>
              <p className="mt-6 inline-flex items-center gap-1.5 text-[11px] text-white/45">
                <ShoppingBag className="h-3.5 w-3.5 text-brand-teal" />
                Sign up once. Shop. Submit PO at checkout.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <StickyMobileCTA />
      <BackToTop />
    </div>
  );
};

export default AgencyPortalPage;
